import React, { useEffect, useState } from 'react';
import { diffLines } from 'diff';
import { useEditor, EditorContent } from '@tiptap/react';
import {
  Box, Paper, Stack, Divider, IconButton, Button, TextField, Tooltip, Chip, CircularProgress,
  Snackbar, Alert, MenuItem, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import RateReviewOutlinedIcon from '@mui/icons-material/RateReviewOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ChecklistIcon from '@mui/icons-material/Checklist';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import SubscriptIcon from '@mui/icons-material/Subscript';
import SuperscriptIcon from '@mui/icons-material/Superscript';
import CodeIcon from '@mui/icons-material/Code';
import TerminalIcon from '@mui/icons-material/Terminal';
import LinkIcon from '@mui/icons-material/Link';
import ImageIcon from '@mui/icons-material/Image';
import TableChartIcon from '@mui/icons-material/TableChart';
import SearchIcon from '@mui/icons-material/Search';
import ApiIcon from '@mui/icons-material/Api';
import SchemaIcon from '@mui/icons-material/Schema';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import SaveIcon from '@mui/icons-material/Save';
import PublishIcon from '@mui/icons-material/Publish';
import { docExtensions, type ApiEndpointAttrs } from '@atlas/docs';
import { useImagePicker } from './MediaPicker';
import { ApiBlockDialog } from './ApiBlockDialog';
import { ApiSpecDialog } from './ApiSpecDialog';
import { LinkDialog, PromptDialog, type LinkValue } from './dialogs';
import type { Api, TenantView, DocPageAdmin, DocComment } from './api';

// Non-technical WYSIWYG doc editor (TipTap). Reuses the SHARED docExtensions so
// what you edit matches what the migration produced and what the site renders.
// Native <select> styled to follow the MUI theme (light/dark) via CSS variables.
const SEL: React.CSSProperties = {
  fontSize: 12, padding: '3px 6px', borderRadius: 6, alignSelf: 'center', cursor: 'pointer',
  border: '1px solid var(--mui-palette-divider)',
  background: 'var(--mui-palette-background-paper)',
  color: 'var(--mui-palette-text-primary)',
};

function TB({ title, active, onClick, children, disabled }: { title: string; active?: boolean; onClick: () => void; children: React.ReactNode; disabled?: boolean }) {
  return (
    <Tooltip title={title}>
      <span>
        <IconButton size="small" color={active ? 'primary' : 'default'} onClick={onClick} disabled={disabled}
          sx={{ bgcolor: active ? 'action.selected' : undefined }}>{children}</IconButton>
      </span>
    </Tooltip>
  );
}

// One line of an inline diff.
function DiffLine({ kind, text }: { kind: 'add' | 'del' | 'ctx'; text: string }) {
  const bg = kind === 'add' ? 'rgba(22,163,74,0.14)' : kind === 'del' ? 'rgba(220,38,38,0.14)' : 'transparent';
  const mark = kind === 'add' ? '+' : kind === 'del' ? '−' : ' ';
  return (
    <Box sx={{ display: 'flex', gap: 1, px: 1, bgcolor: bg }}>
      <Box component="span" sx={{ userSelect: 'none', color: 'text.disabled', width: 12, flexShrink: 0 }}>{mark}</Box>
      <Box component="span" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: kind === 'ctx' ? 'text.secondary' : 'text.primary', textDecoration: kind === 'del' ? 'line-through' : 'none' }}>{text || ' '}</Box>
    </Box>
  );
}

// Inline line-diff of published (base) vs draft, collapsing long unchanged runs.
function DiffView({ published, draft }: { published: string; draft: string }) {
  const parts = diffLines(published || '', draft || '');
  if (!parts.some((p) => p.added || p.removed)) {
    return <Typography variant="body2" color="text.secondary">No textual differences (formatting-only, or identical to the published version).</Typography>;
  }
  const rows: React.ReactNode[] = [];
  let k = 0;
  for (const p of parts) {
    const lines = p.value.replace(/\n$/, '').split('\n');
    if (!p.added && !p.removed) {
      if (lines.length > 6) {
        lines.slice(0, 2).forEach((l) => rows.push(<DiffLine key={k++} kind="ctx" text={l} />));
        rows.push(<Box key={k++} sx={{ px: 1, py: 0.25, color: 'text.disabled', fontSize: 12 }}>… {lines.length - 4} unchanged lines …</Box>);
        lines.slice(-2).forEach((l) => rows.push(<DiffLine key={k++} kind="ctx" text={l} />));
      } else {
        lines.forEach((l) => rows.push(<DiffLine key={k++} kind="ctx" text={l} />));
      }
    } else {
      lines.forEach((l) => rows.push(<DiffLine key={k++} kind={p.added ? 'add' : 'del'} text={l} />));
    }
  }
  return <Box sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 13, lineHeight: 1.6, overflowX: 'auto' }}>{rows}</Box>;
}

export function DocEditor({
  api, tenant, pageId, canPublish, onSaved,
}: { api: Api; tenant: TenantView; pageId: string; canPublish: boolean; onSaved?: () => void }) {
  const [page, setPage] = useState<DocPageAdmin | null>(null);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState<'' | 'save' | 'publish'>('');
  const [dirty, setDirty] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' } | null>(null);
  const [apiOpen, setApiOpen] = useState(false);
  const [apiInitial, setApiInitial] = useState<Partial<ApiEndpointAttrs> | null>(null);
  const [specOpen, setSpecOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkInitial, setLinkInitial] = useState<LinkValue>({ href: 'https://', newTab: false });
  const [access, setAccess] = useState('inherit');
  const [entText, setEntText] = useState(''); // raw text while typing; parsed on save
  const [textAsk, setTextAsk] = useState<{ title: string; label?: string; initial?: string; confirmLabel?: string; resolve: (v: string | null) => void } | null>(null);
  const askText = (o: { title: string; label?: string; initial?: string; confirmLabel?: string }) =>
    new Promise<string | null>((resolve) => setTextAsk({ ...o, resolve }));
  const picker = useImagePicker();
  const [comments, setComments] = useState<DocComment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [showComments, setShowComments] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false); // request-changes dialog
  const [reviewNote, setReviewNote] = useState('');
  const [showDiff, setShowDiff] = useState(false);
  const [diffData, setDiffData] = useState<{ published: string; draft: string } | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  // Docs public URL (Tenant Setup) for the "View live" link. Non-secret.
  const [docsUrl, setDocsUrl] = useState<string | null>(null);
  const [docsEnabled, setDocsEnabled] = useState(true);
  useEffect(() => {
    let alive = true;
    api.tenantConfig(tenant.tenantId).then((c) => { if (alive) { setDocsUrl(c.docsUrl); setDocsEnabled(c.docsEnabled); } }).catch(() => {});
    return () => { alive = false; };
  }, [tenant.tenantId]);

  const editor = useEditor({
    extensions: docExtensions,
    content: '<p></p>',
    onUpdate: () => setDirty(true),
  }, []);

  useEffect(() => {
    setPage(null); setDirty(false);
    api.docPage(tenant.tenantId, pageId).then((p) => {
      setPage(p); setTitle(p.title); setAccess(p.access ?? 'inherit'); setEntText((p.requiredEntitlements ?? []).join(', '));
      editor?.commands.setContent(p.content ?? { type: 'doc', content: [{ type: 'paragraph' }] });
      setDirty(false);
    }).catch((e) => setToast({ msg: e.message, sev: 'error' }));
  }, [pageId, tenant.tenantId, editor]);

  // Refresh page metadata (revisionId / publishedRevisionId / access) WITHOUT
  // resetting the editor content, so the status chip + Save/Publish enablement
  // reflect the latest state after a save/publish.
  const refreshMeta = async () => {
    try { setPage(await api.docPage(tenant.tenantId, pageId)); } catch { /* keep prior */ }
  };

  const loadComments = () => api.docComments(tenant.tenantId, pageId).then(setComments).catch(() => setComments([]));
  useEffect(() => { loadComments(); setShowComments(false); setShowDiff(false); setDiffData(null); }, [pageId, tenant.tenantId]);

  const toggleDiff = async () => {
    if (showDiff) { setShowDiff(false); return; }
    setShowDiff(true);
    if (!diffData) {
      setDiffLoading(true);
      try { setDiffData(await api.docPageDiff(tenant.tenantId, pageId)); }
      catch (e: any) { setToast({ msg: e.message, sev: 'error' }); setShowDiff(false); }
      finally { setDiffLoading(false); }
    }
  };

  const submitReview = async () => {
    try {
      if (dirty) await save();
      await api.submitDocReview(tenant.tenantId, pageId);
      setToast({ msg: 'Submitted for review', sev: 'success' });
      await refreshMeta(); onSaved?.();
    } catch (e: any) { setToast({ msg: `Couldn’t submit: ${e.message}`, sev: 'error' }); }
  };
  const doRequestChanges = async () => {
    try {
      await api.requestDocChanges(tenant.tenantId, pageId, reviewNote.trim());
      setReviewOpen(false); setReviewNote('');
      setToast({ msg: 'Changes requested', sev: 'success' });
      await refreshMeta(); await loadComments(); onSaved?.();
    } catch (e: any) { setToast({ msg: e.message, sev: 'error' }); }
  };
  const addComment = async () => {
    const b = commentText.trim(); if (!b) return;
    try { await api.addDocComment(tenant.tenantId, pageId, b); setCommentText(''); await loadComments(); } catch (e: any) { setToast({ msg: e.message, sev: 'error' }); }
  };
  const toggleResolve = async (c: DocComment) => {
    try { await api.resolveDocComment(tenant.tenantId, c.id, !c.resolved); await loadComments(); } catch { /* ignore */ }
  };

  const save = async (): Promise<boolean> => {
    if (!editor) return false;
    setBusy('save');
    try {
      await api.saveDocRevision(tenant.tenantId, pageId, editor.getJSON(), title.trim());
      setDirty(false); setToast({ msg: 'Draft saved', sev: 'success' });
      await refreshMeta(); onSaved?.();
      return true;
    } catch (e: any) { setToast({ msg: `Save failed: ${e.message}`, sev: 'error' }); return false; }
    finally { setBusy(''); }
  };
  const publish = async () => {
    setBusy('publish');
    try {
      if (dirty && !(await saveInline())) return;
      await api.publishDocPage(tenant.tenantId, pageId);
      setToast({ msg: 'Published — live on the docs site', sev: 'success' });
      await refreshMeta(); onSaved?.();
    } catch (e: any) { setToast({ msg: `Publish failed: ${e.message}`, sev: 'error' }); }
    finally { setBusy(''); }
  };
  // save without toggling the save-button spinner (used inside publish)
  const saveInline = async () => {
    if (!editor) return false;
    try { await api.saveDocRevision(tenant.tenantId, pageId, editor.getJSON(), title.trim()); setDirty(false); return true; }
    catch (e: any) { setToast({ msg: `Save failed: ${e.message}`, sev: 'error' }); return false; }
  };

  const addImage = async () => {
    if (!editor) return;
    // If an image is selected, the button edits its caption instead of inserting.
    if (editor.isActive('image')) {
      const current = (editor.getAttributes('image').caption as string) || '';
      const cap = await askText({ title: 'Image caption', label: 'Caption', initial: current, confirmLabel: 'Save caption' });
      if (cap !== null) editor.chain().focus().updateAttributes('image', { caption: cap.trim() || null }).run();
      return;
    }
    const url = picker ? await picker.pick() : window.prompt('Image URL') || '';
    if (!url) return;
    // Optional caption on insert — cancel to skip.
    const cap = await askText({ title: 'Image caption (optional)', label: 'Caption', initial: '', confirmLabel: 'Insert image' });
    editor.chain().focus().setImage({ src: url, caption: (cap ?? '').trim() || null } as any).run();
  };
  const openApi = () => {
    if (!editor) return;
    setApiInitial(editor.isActive('apiEndpoint') ? (editor.getAttributes('apiEndpoint') as ApiEndpointAttrs) : null);
    setApiOpen(true);
  };
  const saveApi = (attrs: ApiEndpointAttrs) => {
    if (!editor) return;
    if (editor.isActive('apiEndpoint')) editor.chain().focus().updateAttributes('apiEndpoint', attrs).run();
    else editor.chain().focus().insertContent({ type: 'apiEndpoint', attrs }).run();
    setApiOpen(false);
  };
  const saveAccess = async (a: string) => {
    setAccess(a);
    const ents = a === 'protected' ? entText.split(',').map((x) => x.trim()).filter(Boolean) : [];
    try {
      await api.setDocAccess(tenant.tenantId, pageId, a, ents);
      setToast({ msg: 'Access updated', sev: 'success' });
      await refreshMeta(); onSaved?.();
    } catch (e: any) { setToast({ msg: `Couldn’t update access: ${e.message}`, sev: 'error' }); }
  };
  const saveSpec = (spec: any) => {
    if (!editor) return;
    editor.chain().focus().insertContent({ type: 'apiSpec', attrs: { spec } }).run();
    setSpecOpen(false);
  };
  const openLink = () => {
    if (!editor) return;
    const attrs = editor.getAttributes('link');
    setLinkInitial({ href: (attrs.href as string) || 'https://', newTab: attrs.target === '_blank' });
    setLinkOpen(true);
  };
  const applyLink = (v: LinkValue | null) => {
    setLinkOpen(false);
    if (!editor || v === null) return;
    // Store the author's choice explicitly: '_blank' = new tab, '_self' = same tab.
    editor.chain().focus().extendMarkRange('link').setLink({ href: v.href, target: v.newTab ? '_blank' : '_self' }).run();
  };

  if (!page) return <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box>;

  const published = Boolean(page.publishedRevisionId);
  const draftAhead = dirty || (!!page.revisionId && page.revisionId !== page.publishedRevisionId);
  const nothingToPublish = published && !draftAhead;
  const inReview = page.reviewStatus === 'IN_REVIEW';

  return (
    <Box>
      {/* Header — fixed-width title (wraps) on the left, controls on the right */}
      <Stack direction="row" alignItems="flex-start" spacing={2} sx={{ mb: 2 }}>
        <Box sx={{ width: 360, flexShrink: 0 }}>
          <TextField
            value={title} onChange={(e) => { setTitle(e.target.value); setDirty(true); }}
            variant="standard" placeholder="Page title" fullWidth multiline
            InputProps={{ disableUnderline: true }}
            sx={{ '& textarea': { fontSize: 24, fontWeight: 800, lineHeight: 1.25, padding: 0 } }}
          />
          <Box sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, color: 'text.secondary', mt: 0.5 }}>/{page.path}</Box>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexWrap: 'wrap', rowGap: 1, justifyContent: 'flex-end', pt: 0.5 }}>
          <Chip
            size="small"
            label={inReview ? 'In review' : !published ? 'Draft' : draftAhead ? 'Unpublished changes' : 'Published'}
            color={inReview ? 'info' : !published ? 'default' : draftAhead ? 'warning' : 'success'}
            variant={inReview ? 'filled' : draftAhead && published ? 'outlined' : 'filled'}
          />
          <TextField select size="small" label="Access" value={access} onChange={(e) => saveAccess(e.target.value)} sx={{ width: 150 }}>
            <MenuItem value="inherit">Inherit</MenuItem>
            <MenuItem value="public">Public</MenuItem>
            <MenuItem value="protected">🔒 Protected</MenuItem>
          </TextField>
          {access === 'protected' ? (
            <Tooltip title="Comma-separated entitlements a reader must hold. Saves when you click away.">
              <TextField
                size="small" label="Entitlements" placeholder="pro, enterprise"
                value={entText}
                onChange={(e) => setEntText(e.target.value)}
                onBlur={() => saveAccess('protected')}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                sx={{ width: 220 }}
              />
            </Tooltip>
          ) : null}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
          {docsUrl && docsEnabled && page ? (
            <Button size="small" startIcon={<OpenInNewIcon />} href={`${docsUrl.replace(/\/+$/, '')}/${page.path}`} target="_blank" rel="noreferrer">
              View live
            </Button>
          ) : null}
          <Button size="small" variant="outlined" startIcon={<SaveIcon />} onClick={save} disabled={!dirty || !!busy}>
            {busy === 'save' ? 'Saving…' : 'Save draft'}
          </Button>
          {!inReview && draftAhead ? (
            <Button size="small" variant="outlined" color="info" startIcon={<RateReviewOutlinedIcon />} onClick={submitReview} disabled={!!busy}>
              Submit for review
            </Button>
          ) : null}
          {canPublish && inReview ? (
            <Button size="small" variant="outlined" color="warning" onClick={() => setReviewOpen(true)} disabled={!!busy}>
              Request changes
            </Button>
          ) : null}
          {canPublish ? (
            <Tooltip title={nothingToPublish ? 'Nothing new to publish — this version is already live' : 'Publish the current draft to the live docs site'}>
              <span>
                <Button size="small" variant="contained" startIcon={<PublishIcon />} onClick={publish} disabled={!!busy || nothingToPublish}>
                  {busy === 'publish' ? 'Publishing…' : 'Publish'}
                </Button>
              </span>
            </Tooltip>
          ) : null}
        </Stack>
      </Stack>

      {(inReview || comments.length > 0) ? (
        <Alert severity={inReview ? 'info' : 'success'} sx={{ mb: 2, '& .MuiAlert-message': { width: '100%', minWidth: 0 } }}>
          <Box sx={{ mb: 1 }}>
            {inReview
              ? <>This draft is <b>in review</b>{page.source !== 'human' ? ` (submitted via ${page.source})` : ''}.{page.changeNote ? <> — “{page.changeNote}”</> : null} {canPublish ? 'Edit and Publish, or Request changes.' : 'A reviewer will publish or request changes.'}</>
              : <>Review notes on this page.</>}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {page.publishedRevisionId ? (
              <Button size="small" variant="outlined" color="inherit" onClick={toggleDiff} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{showDiff ? 'Hide diff' : 'Changes'}</Button>
            ) : null}
            <Button size="small" variant="outlined" color="inherit" onClick={() => setShowComments((v) => !v)} sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>{showComments ? 'Hide comments' : `Comments (${comments.length})`}</Button>
          </Box>
        </Alert>
      ) : null}

      {showDiff ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Changes vs published{' '}
            <Typography component="span" variant="caption" color="text.secondary">(green = added, red = removed)</Typography>
          </Typography>
          {diffLoading ? <CircularProgress size={20} /> : diffData ? <DiffView published={diffData.published} draft={diffData.draft} /> : null}
        </Paper>
      ) : null}

      {showComments ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          {comments.length === 0 ? <Typography variant="body2" color="text.secondary">No comments yet.</Typography> : comments.map((c) => (
            <Box key={c.id} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', py: 1, borderBottom: '1px solid', borderColor: 'divider', opacity: c.resolved ? 0.55 : 1 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">{c.authorLabel || 'Reviewer'} · {new Date(c.createdAt).toLocaleString()}{c.resolved ? ' · resolved' : ''}</Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{c.body}</Typography>
              </Box>
              <Button size="small" onClick={() => toggleResolve(c)}>{c.resolved ? 'Reopen' : 'Resolve'}</Button>
            </Box>
          ))}
          <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
            <TextField size="small" fullWidth multiline placeholder="Add a review note…" value={commentText} onChange={(e) => setCommentText(e.target.value)} />
            <Button variant="contained" onClick={addComment} disabled={!commentText.trim()}>Add</Button>
          </Box>
        </Paper>
      ) : null}

      <Paper variant="outlined">
        {editor ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.25, p: 0.5, borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, bgcolor: 'background.paper', zIndex: 1 }}>
            <select
              value={(() => { const l = [1, 2, 3, 4, 5, 6].find((x) => editor.isActive('heading', { level: x })); return l ? `h${l}` : 'p'; })()}
              onChange={(e) => { const v = e.target.value; if (v === 'p') editor.chain().focus().setParagraph().run(); else editor.chain().focus().setHeading({ level: Number(v[1]) as any }).run(); }}
              style={SEL}
            >
              <option value="p">Paragraph</option>
              {[1, 2, 3, 4, 5, 6].map((l) => <option key={l} value={`h${l}`}>Heading {l}</option>)}
            </select>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <TB title="Bold" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><FormatBoldIcon fontSize="small" /></TB>
            <TB title="Italic" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><FormatItalicIcon fontSize="small" /></TB>
            <TB title="Underline" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><FormatUnderlinedIcon fontSize="small" /></TB>
            <TB title="Strikethrough" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()}><StrikethroughSIcon fontSize="small" /></TB>
            <TB title="Highlight" active={editor.isActive('highlight')} onClick={() => editor.chain().focus().toggleHighlight().run()}><BorderColorIcon fontSize="small" /></TB>
            <TB title="Inline code" active={editor.isActive('code')} onClick={() => editor.chain().focus().toggleCode().run()}><CodeIcon fontSize="small" /></TB>
            <TB title="Subscript" active={editor.isActive('subscript')} onClick={() => editor.chain().focus().toggleSubscript().run()}><SubscriptIcon fontSize="small" /></TB>
            <TB title="Superscript" active={editor.isActive('superscript')} onClick={() => editor.chain().focus().toggleSuperscript().run()}><SuperscriptIcon fontSize="small" /></TB>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <TB title="Bulleted list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><FormatListBulletedIcon fontSize="small" /></TB>
            <TB title="Numbered list" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><FormatListNumberedIcon fontSize="small" /></TB>
            <TB title="Task list" active={editor.isActive('taskList')} onClick={() => editor.chain().focus().toggleTaskList().run()}><ChecklistIcon fontSize="small" /></TB>
            <TB title="Quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}><FormatQuoteIcon fontSize="small" /></TB>
            <TB title="Divider" onClick={() => editor.chain().focus().setHorizontalRule().run()}><HorizontalRuleIcon fontSize="small" /></TB>
            <select
              value={editor.isActive('callout') ? (editor.getAttributes('callout').type as string) : ''}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) editor.chain().focus().unsetCallout().run();
                else if (editor.isActive('callout')) editor.chain().focus().updateAttributes('callout', { type: v }).run();
                else editor.chain().focus().setCallout({ type: v as any }).run();
              }}
              style={SEL}
            >
              <option value="">Callout…</option>
              {['info', 'note', 'tip', 'warning', 'danger'].map((c) => <option key={c} value={c}>{c[0]!.toUpperCase() + c.slice(1)}</option>)}
            </select>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <TB title="Code block" active={editor.isActive('codeBlock')} onClick={() => editor.chain().focus().toggleCodeBlock().run()}><TerminalIcon fontSize="small" /></TB>
            {editor.isActive('codeBlock') ? (
              <select
                value={(editor.getAttributes('codeBlock').language as string) || ''}
                onChange={(e) => editor.chain().focus().updateAttributes('codeBlock', { language: e.target.value || null }).run()}
                style={SEL}
              >
                <option value="">plain</option>
                {['bash', 'json', 'javascript', 'typescript', 'jsx', 'tsx', 'python', 'yaml', 'xml', 'html', 'sql', 'http', 'java', 'csharp', 'go', 'php', 'powershell'].map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            ) : null}
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <TB title="Link" active={editor.isActive('link')} onClick={openLink}><LinkIcon fontSize="small" /></TB>
            <TB title="Image" onClick={addImage}><ImageIcon fontSize="small" /></TB>
            <TB title="Table" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}><TableChartIcon fontSize="small" /></TB>
            {editor.isActive('table') ? (
              <TB
                title={editor.getAttributes('table').searchable ? 'Table search: ON (site shows a filter box) — click to turn off' : 'Table search: OFF — click to add a filter box on the site'}
                active={!!editor.getAttributes('table').searchable}
                onClick={() => editor.chain().focus().updateAttributes('table', { searchable: !editor.getAttributes('table').searchable }).run()}
              ><SearchIcon fontSize="small" /></TB>
            ) : null}
            <TB title="API endpoint" active={editor.isActive('apiEndpoint')} onClick={openApi}><ApiIcon fontSize="small" /></TB>
            <TB title="API reference (OpenAPI)" onClick={() => setSpecOpen(true)}><SchemaIcon fontSize="small" /></TB>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <TB title="Undo" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}><UndoIcon fontSize="small" /></TB>
            <TB title="Redo" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}><RedoIcon fontSize="small" /></TB>
          </Box>
        ) : null}
        <Box className="doc-editor" sx={{
          p: 3, minHeight: '55vh',
          '& .ProseMirror': { outline: 'none', minHeight: '50vh' },
          '& .ProseMirror h1': { fontSize: 28, fontWeight: 800, margin: '.6em 0 .35em' },
          '& .ProseMirror h2': { fontSize: 22, fontWeight: 700, margin: '1.2em 0 .4em' },
          '& .ProseMirror h3': { fontSize: 18, fontWeight: 700, margin: '1em 0 .3em' },
          '& .ProseMirror h4': { fontSize: 16, fontWeight: 700, margin: '.9em 0 .3em' },
          '& .ProseMirror h5': { fontSize: 14, fontWeight: 700, margin: '.8em 0 .2em' },
          '& .ProseMirror h6': { fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.secondary', margin: '.8em 0 .2em' },
          '& .ProseMirror p': { margin: '0 0 .8em' },
          '& .ProseMirror hr': { border: 'none', borderTop: '1px solid #e5e8ec', margin: '20px 0' },
          '& .ProseMirror mark': { background: '#fde68a', padding: '0 2px', borderRadius: '2px' },
          '& .ProseMirror sub, & .ProseMirror sup': { fontSize: '.75em' },
          // Task lists
          '& .ProseMirror ul[data-type="taskList"]': { listStyle: 'none', padding: 0 },
          '& .ProseMirror ul[data-type="taskList"] li': { display: 'flex', gap: '8px', alignItems: 'flex-start' },
          '& .ProseMirror ul[data-type="taskList"] li > label': { marginTop: '3px', userSelect: 'none' },
          '& .ProseMirror ul[data-type="taskList"] li > div': { flex: 1 },
          '& .ProseMirror ul[data-type="taskList"] li > div > p': { margin: 0 },
          // Callouts
          '& .ProseMirror .callout': { borderRadius: '8px', padding: '10px 14px 10px 16px', margin: '14px 0', borderLeft: '4px solid', background: '#f1f5f9' },
          '& .ProseMirror .callout > :last-child': { marginBottom: 0 },
          '& .ProseMirror .callout-info': { borderColor: '#0b7a75', background: 'rgba(11,122,117,.07)' },
          '& .ProseMirror .callout-note': { borderColor: '#64748b', background: 'rgba(100,116,139,.08)' },
          '& .ProseMirror .callout-tip': { borderColor: '#2e7d32', background: 'rgba(46,125,50,.08)' },
          '& .ProseMirror .callout-warning': { borderColor: '#b4690e', background: 'rgba(180,105,14,.09)' },
          '& .ProseMirror .callout-danger': { borderColor: '#c0392b', background: 'rgba(192,57,43,.08)' },
          '& .ProseMirror img': { maxWidth: '100%', borderRadius: '4px', border: '1px solid #e5e8ec' },
          // Tables + column resizing (drag a column border to resize). table-layout
          // fixed + position:relative cells are required for the resize handles.
          '& .ProseMirror .tableWrapper': { overflowX: 'auto', margin: '12px 0' },
          '& .ProseMirror table': { borderCollapse: 'collapse', width: '100%', tableLayout: 'fixed', margin: '0' },
          '& .ProseMirror td, & .ProseMirror th': { border: '1px solid #d7dce2', padding: '6px 10px', position: 'relative', verticalAlign: 'top', minWidth: '2em', boxSizing: 'border-box' },
          '& .ProseMirror .column-resize-handle': { position: 'absolute', right: '-2px', top: 0, bottom: 0, width: '4px', background: '#6d5cff', cursor: 'col-resize', zIndex: 20 },
          '& .ProseMirror.resize-cursor': { cursor: 'col-resize' },
          '& .ProseMirror .selectedCell:after': { content: '""', position: 'absolute', inset: 0, background: 'rgba(109,92,255,0.12)', pointerEvents: 'none' },
          // Search-enabled tables get a subtle accent outline so it's visible at a glance.
          '& .ProseMirror table[data-searchable="true"]': { outline: '2px solid rgba(109,92,255,0.45)', outlineOffset: '3px' },
          '& .ProseMirror blockquote': { borderLeft: '3px solid #0b7a75', margin: '12px 0', paddingLeft: 14, color: 'text.secondary' },
          // Code block with line-number gutter (NodeView renders .cb > gutter + pre>code)
          '& .ProseMirror .cb': { display: 'flex', background: '#0f172a', borderRadius: '16px', overflow: 'hidden', margin: '12px 0', border: '1px solid #1e293b' },
          '& .ProseMirror .cb-gutter': { flex: 'none', padding: '14px 10px', textAlign: 'right', color: '#5b6675', userSelect: 'none', whiteSpace: 'pre', borderRight: '1px solid rgba(255,255,255,0.08)', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '13px', lineHeight: 1.6 },
          '& .ProseMirror .cb pre': { flex: 1, margin: 0, background: 'transparent', padding: '14px 16px', overflow: 'auto' },
          '& .ProseMirror .cb code': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', fontSize: '13px', lineHeight: 1.6, color: '#e2e8f0', whiteSpace: 'pre', background: 'none', padding: 0 },
          '& .ProseMirror :not(pre) > code': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', background: 'rgba(0,0,0,.06)', padding: '1px 5px', borderRadius: '4px', fontSize: '.9em' },
          // API endpoint block
          '& .api-endpoint': { border: '1px solid', borderColor: 'divider', borderRadius: '12px', padding: '16px', margin: '16px 0', background: '#fbfcfd' },
          '& .api-endpoint .api-head': { display: 'flex', alignItems: 'center', gap: 1, mb: 1 },
          '& .api-endpoint .api-method': { fontFamily: 'ui-monospace, monospace', fontWeight: 700, fontSize: '12px', padding: '2px 8px', borderRadius: '5px', color: '#fff', marginRight: '10px' },
          '& .api-endpoint .m-get': { background: '#0b7a75' }, '& .api-endpoint .m-post': { background: '#2e7d32' },
          '& .api-endpoint .m-put': { background: '#b4690e' }, '& .api-endpoint .m-patch': { background: '#8b5cf6' }, '& .api-endpoint .m-delete': { background: '#c0392b' },
          '& .api-endpoint .api-path': { fontFamily: 'ui-monospace, monospace', fontSize: '14px', fontWeight: 600 },
          '& .api-endpoint .api-summary': { fontWeight: 600, margin: '6px 0' },
          '& .api-endpoint .api-desc': { color: 'text.secondary', margin: '4px 0' },
          '& .api-endpoint .api-label': { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.5px', color: 'text.secondary', margin: '12px 0 6px' },
          '& .api-endpoint .api-table': { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
          '& .api-endpoint .api-table th, & .api-endpoint .api-table td': { border: '1px solid', borderColor: 'divider', padding: '6px 10px', textAlign: 'left' },
          '& .api-endpoint .api-table th': { background: '#eef1f4', fontSize: '11px', textTransform: 'uppercase', color: 'text.secondary' },
          '& .api-endpoint .api-auth': { display: 'flex', alignItems: 'center', gap: '8px', margin: '6px 0', fontSize: '13px' },
          '& .api-endpoint .api-auth-label': { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', color: 'text.secondary' },
          '& .api-endpoint .api-samples': { display: 'flex', flexDirection: 'column' },
          '& .api-endpoint .api-sample-lang': { fontSize: '11px', fontWeight: 700, color: 'text.secondary', margin: '10px 0 4px' },
          '& .api-endpoint pre': { background: '#0f172a', color: '#e2e8f0', padding: '12px 14px', borderRadius: '10px', overflow: 'auto', margin: '4px 0' },
          '& .api-endpoint pre code': { fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: '12.5px', background: 'none', color: 'inherit', padding: 0, whiteSpace: 'pre' },
          // OpenAPI reference wrapper
          '& .api-spec .oas-title': { fontSize: '20px', fontWeight: 800 },
          '& .api-spec .oas-ver': { fontSize: '13px', color: 'text.secondary', fontWeight: 600 },
          '& .api-spec .oas-base': { fontSize: '13px', margin: '6px 0' },
          '& .api-spec .oas-tag': { fontSize: '16px', fontWeight: 700, margin: '22px 0 10px', borderBottom: '1px solid', borderColor: 'divider', paddingBottom: '4px' },
          '& .api-spec .oas-error': { color: 'error.main', fontSize: '13px' },
          '& .tryit': { display: 'none' }, // the "try it" console only works on the docs site (proxy)
        }}>
          <EditorContent editor={editor} />
        </Box>
      </Paper>

      <ApiBlockDialog open={apiOpen} initial={apiInitial} onClose={() => setApiOpen(false)} onSave={saveApi} />
      <ApiSpecDialog open={specOpen} onClose={() => setSpecOpen(false)} onSave={saveSpec} />
      <LinkDialog open={linkOpen} initial={linkInitial} onClose={applyLink} />
      <PromptDialog
        open={!!textAsk} title={textAsk?.title ?? ''} label={textAsk?.label} initial={textAsk?.initial ?? ''}
        confirmLabel={textAsk?.confirmLabel} placeholder="e.g. Time Based Config"
        onClose={(v) => { textAsk?.resolve(v); setTextAsk(null); }}
      />
      <Dialog open={reviewOpen} onClose={() => setReviewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request changes</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth multiline minRows={3} margin="dense" label="What needs to change?"
            value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} />
          <Typography variant="caption" color="text.secondary">Sends the draft back and adds this note as a comment the author (or API) can read.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewOpen(false)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={doRequestChanges} disabled={!reviewNote.trim()}>Request changes</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.sev} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
}
